import { AnimatePresence, motion } from "motion/react";
import {
  Children,
  cloneElement,
  forwardRef,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import {
  mergeProps,
  OverlayContainer,
  useOverlay,
  useOverlayPosition,
  useTooltip as useReactAriaTooltip,
  useTooltipTrigger,
} from "react-aria";
import { useTooltipTriggerState } from "react-stately";

import { useDomRef } from "../../hooks/useDomRef";
import { useElementOverflowing } from "../../hooks/useElementOverflowing";
import { cn } from "../../utils/cn";
import { getArrowPlacement, getTransformOrigins, toReactAriaPlacement } from "../../utils/overlay";
import { getElementRef, mergeRefs } from "../../utils/refs";
import { scaleSpring } from "../../utils/transitionVariants";
import { popoverVariants } from "../popover/usePopover";

import type { HTMLMotionProps } from "motion/react";
import type { HTMLAttributes, ReactNode, Ref } from "react";
import type { AriaOverlayProps, AriaTooltipProps } from "react-aria";
import type { OverlayTriggerProps } from "react-stately";
import type { OverlayOptions } from "../../utils/overlay";
import type { ReactRef, SlotsToClasses } from "../../utils/types";
import type { PopoverVariantProps } from "../popover/usePopover";

interface BaseProps extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
  /**
   * Ref to the DOM node.
   */
  ref?: ReactRef<HTMLElement | null>;
  /**
   * The children to render. Usually a trigger element.
   */
  children?: ReactNode;
  /**
   * The content of the tooltip.
   */
  content?: string | React.ReactNode;
  /**
   * Whether the tooltip should be disabled, independent from the trigger.
   */
  isDisabled?: boolean;
  /**
   * The delay time in ms for the tooltip to show up.
   * @default 500
   */
  delay?: number;
  /**
   * The delay time in ms for the tooltip to hide.
   * @default 0
   */
  closeDelay?: number;
  /**
   * By default, opens for both focus and hover. Can be made to open only for focus.
   */
  trigger?: "focus";
  /**
   * The props to modify the framer motion animation. Use the `variants` API to create your own animation.
   */
  motionProps?: HTMLMotionProps<"div">;
  /**
   * The container element in which the overlay portal will be placed.
   * @default document.body
   */
  portalContainer?: Element;
  /**
   * List of dependencies to update the position of the tooltip.
   * @default []
   */
  updatePositionDeps?: any[];
  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <Tooltip classNames={{
   *    base:"base-classes",
   *    content: "content-classes",
   *    arrow: "arrow-classes",
   * }} />
   * ```
   */
  classNames?: SlotsToClasses<"base" | "arrow" | "content">;
}

export type TooltipProps = BaseProps &
  AriaTooltipProps &
  AriaOverlayProps &
  OverlayTriggerProps &
  OverlayOptions &
  PopoverVariantProps;

/**
 * A Tooltip displays information about a specific element when the user hovers over, focuses on,
 * or touches the element.
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
  const {
    isOpen: isOpenProp,
    content,
    children,
    defaultOpen,
    onOpenChange,
    isDisabled,
    trigger: triggerAction,
    shouldFlip = true,
    containerPadding = 12,
    placement: placementProp = "top",
    delay = 500,
    closeDelay = 0,
    showArrow = true,
    offset = 7,
    crossOffset = 0,
    isDismissable,
    shouldCloseOnBlur = true,
    portalContainer,
    isKeyboardDismissDisabled = false,
    updatePositionDeps = [],
    shouldCloseOnInteractOutside,
    className,
    onClose,
    motionProps,
    classNames,
    size,
    color = "inverted",
    backdrop,
    triggerScaleOnOpen,
    disableAnimation,
    ...otherProps
  } = props;

  // Don't show tooltip if content is empty (matches AntD Tooltip behavior)
  const hasEmptyContent = !content || (typeof content === "string" && content.trim() === "");
  const effectiveIsDisabled = isDisabled || hasEmptyContent;

  const state = useTooltipTriggerState({
    delay,
    closeDelay,
    isDisabled: effectiveIsDisabled,
    defaultOpen,
    isOpen: isOpenProp,
    onOpenChange: isOpen => {
      onOpenChange?.(isOpen);
      if (!isOpen) {
        onClose?.();
      }
    },
  });

  const triggerRef = useRef<HTMLElement>(null);
  const overlayRef = useDomRef(ref);

  const tooltipId = useId();

  const isOpen = state.isOpen && !isDisabled;

  const { triggerProps, tooltipProps: triggerTooltipProps } = useTooltipTrigger(
    {
      isDisabled,
      trigger: triggerAction,
    },
    state,
    triggerRef
  );

  const { tooltipProps: ariaTooltipProps } = useReactAriaTooltip(
    {
      isOpen,
      ...mergeProps(props, triggerTooltipProps),
    },
    state
  );

  const {
    overlayProps: positionProps,
    placement,
    updatePosition,
  } = useOverlayPosition({
    isOpen: isOpen,
    targetRef: triggerRef,
    placement: toReactAriaPlacement(placementProp),
    overlayRef,
    offset: showArrow ? offset + 3 : offset,
    crossOffset,
    shouldFlip,
    containerPadding,
  });

  useLayoutEffect(() => {
    if (!updatePositionDeps.length) return;
    // force update position when deps change
    updatePosition();
  }, updatePositionDeps);

  const { overlayProps } = useOverlay(
    {
      isOpen: isOpen,
      onClose: state.close,
      isDismissable,
      shouldCloseOnBlur,
      isKeyboardDismissDisabled,
      shouldCloseOnInteractOutside,
    },
    overlayRef
  );

  const slots = useMemo(
    () =>
      popoverVariants({
        size,
        color,
        backdrop,
        triggerScaleOnOpen,
        disableAnimation,
      }),
    [size, color, backdrop, triggerScaleOnOpen, disableAnimation]
  );

  const getTriggerProps = useCallback(
    (props = {}, _ref: Ref<any> | null | undefined = null) => ({
      ...mergeProps(triggerProps, props),
      ref: mergeRefs(_ref, triggerRef),
      "aria-describedby": isOpen ? tooltipId : undefined,
    }),
    [triggerProps, isOpen, tooltipId, state]
  );

  const tooltipProps = useMemo(
    () => ({
      ref: overlayRef,
      "data-slot": "base",
      "data-open": isOpen,
      "data-arrow": showArrow,
      "data-disabled": isDisabled,
      "data-placement": getArrowPlacement(placement!, placementProp),
      ...mergeProps(ariaTooltipProps, overlayProps, otherProps),
      style: mergeProps(positionProps.style, otherProps.style, props.style),
      className: slots.base({ class: classNames?.base }),
      id: tooltipId,
    }),
    [
      slots,
      isOpen,
      showArrow,
      isDisabled,
      placement,
      placementProp,
      ariaTooltipProps,
      overlayProps,
      otherProps,
      positionProps,
      props,
      tooltipId,
      classNames?.base,
      overlayRef,
    ]
  );

  const tooltipContentProps = useMemo(
    () => ({
      "data-slot": "content",
      "data-open": isOpen,
      "data-arrow": showArrow,
      "data-disabled": isDisabled,
      "data-placement": getArrowPlacement(placement!, placementProp),
      className: slots.content({ class: cn(classNames?.content, className) }),
    }),
    [slots, isOpen, showArrow, isDisabled, placement, placementProp, classNames, className]
  );

  // If children is null/undefined/false (e.g. hidden by a feature flag), render nothing.
  // This prevents silent <span /> fallback that causes invisible elements in the UI.
  if (children == null || children === false) {
    return null;
  }

  let trigger: React.ReactElement<unknown>;

  try {
    /**
     * Ensure tooltip has only one child node
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const child = Children.only(children) as React.ReactElement<any>;

    trigger = cloneElement(child, getTriggerProps(child.props, getElementRef(child)));
  } catch {
    trigger = <span />;
    console.warn("Tooltip must have only one child node.");
  }

  const { ref: _, id, style, ...otherTooltipProps } = tooltipProps;

  const animatedContent = (
    <div ref={overlayRef} id={id} style={style}>
      <motion.div
        animate="enter"
        exit="exit"
        initial="exit"
        variants={scaleSpring}
        {...mergeProps(motionProps, otherTooltipProps)}
        style={{
          ...getTransformOrigins(placementProp),
        }}
      >
        <div {...tooltipContentProps}>{content}</div>
      </motion.div>
    </div>
  );

  return (
    <>
      {trigger}
      {!hasEmptyContent && disableAnimation && isOpen ? (
        <OverlayContainer portalContainer={portalContainer}>
          <div ref={overlayRef} id={id} style={style} {...otherTooltipProps}>
            <div {...tooltipContentProps}>{content}</div>
          </div>
        </OverlayContainer>
      ) : (
        <AnimatePresence>
          {!hasEmptyContent && isOpen ? (
            <OverlayContainer portalContainer={portalContainer}>{animatedContent}</OverlayContainer>
          ) : null}
        </AnimatePresence>
      )}
    </>
  );
});

Tooltip.displayName = "Tooltip";

export type TooltipOverflowTextProps = HTMLAttributes<HTMLDivElement> & {
  delay?: number;
  tooltipClassName?: string;
};

export const TooltipOverflowText = forwardRef<HTMLDivElement, TooltipOverflowTextProps>(
  ({ className, delay, tooltipClassName, children, ...props }, ref) => {
    const contentRef = useRef<HTMLSpanElement>(null);
    const textOverflowing = useElementOverflowing(contentRef);
    return (
      <Tooltip
        isDisabled={!textOverflowing}
        content={children}
        delay={delay}
        ref={ref}
        className={cn("w-80", tooltipClassName)}
      >
        <span className={cn("block truncate", className)} ref={contentRef} {...props}>
          {children}
        </span>
      </Tooltip>
    );
  }
);
TooltipOverflowText.displayName = "TooltipOverflowText";
